import React, { useCallback, useEffect, useRef, useState } from "react";
import { History } from "lucide-react";
import { Link } from "react-router-dom";
import { getRequest } from "../helpers/api.helper";

const money = (paise: number = 0) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(paise / 100);
type Wallet = { balance: number; totalDeposits: number; totalSpent: number; totalEarnings: number; totalEarningsWithdrawn: number; contactViewCost: number };

export default function WalletPanel() {
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const requestSequence = useRef(0);

  const load = useCallback(async () => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    setError("");
    try {
      const response = await getRequest("/bureau/wallet");
      if (sequence !== requestSequence.current) return;
      if (response.success && response.data) setWallet(response.data);
      else setError(response.message || "Unable to load wallet information.");
    } catch {
      if (sequence === requestSequence.current) setError("Unable to load wallet information.");
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    return () => { requestSequence.current += 1; };
  }, [load]);
  useEffect(() => {
    const refresh = () => { void load(); };
    window.addEventListener("bureau-wallet-updated", refresh);
    return () => window.removeEventListener("bureau-wallet-updated", refresh);
  }, [load]);

  return (
    <section className="rounded-2xl border border-purple-100 bg-white p-4 shadow-sm sm:p-6" aria-label="Bureau Wallet and Earnings">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-gray-900 sm:text-xl">Bureau Wallet &amp; Earnings</h2>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => void load()} disabled={loading} className="rounded-lg border border-purple-200 px-4 py-2 text-sm font-semibold text-purple-700 disabled:opacity-50">{loading ? "Loading…" : "Refresh"}</button>
          <Link to="/admin/wallet/transactions" aria-label="View transaction history" title="Transaction History" className="inline-flex items-center justify-center rounded-lg border border-purple-200 p-2 text-purple-700 transition hover:bg-purple-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500">
            <History size={20} aria-hidden="true" />
          </Link>
        </div>
      </div>
      {error && <p role="alert" className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {wallet && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <section className="min-w-0 space-y-3 rounded-xl border border-purple-100 bg-purple-50/60 p-3 sm:p-4" aria-label="Wallet">
              <h3 className="font-semibold text-purple-900">Paid Earnings</h3>
              <div><p className="text-xs text-gray-600 sm:text-sm">Wallet Balance</p><p className="mt-1 break-words text-lg font-bold text-purple-800 sm:text-2xl">{money(wallet.balance)}</p></div>
              <div><p className="text-xs text-gray-600 sm:text-sm">Total Contact Spending</p><p className="mt-1 break-words text-lg font-bold text-purple-800 sm:text-2xl">{money(wallet.totalSpent)}</p></div>
            </section>
            <section className="min-w-0 space-y-3 rounded-xl border border-emerald-100 bg-emerald-50/50 p-3 sm:p-4" aria-label="Earnings">
              <h3 className="font-semibold text-emerald-900">FreeEarnings</h3>
              <div className="grid gap-3">
                <div><p className="text-xs text-gray-600 sm:text-sm">Total Earnings</p><p className="mt-1 break-words text-lg font-bold text-emerald-700 sm:text-2xl">{money(wallet.totalEarnings)}</p></div>
                <div><p className="text-xs text-gray-600 sm:text-sm">Earnings Paid</p><p className="mt-1 break-words text-lg font-bold text-gray-800 sm:text-2xl">{money(wallet.totalEarningsWithdrawn)}</p></div>
              </div>
            </section>
          </div>
          <p className="mt-3 text-sm text-gray-600">Current contact view cost: <strong>{money(wallet.contactViewCost)}</strong>. Earnings are separate from your wallet. The Super Admin pays earnings directly and records each payment in transaction history.</p>
        </>
      )}
    </section>
  );
}
